import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SymbolKey } from '@/entities/symbol';
import { exportApi } from '@/features/DataHub/api/export.api';
import { pastExportsApi } from '@/features/DataHub/api/pastExports.api';
import { pastExportsOptions } from '@/features/DataHub/api/pastExports.options';
import { DATAHUB_DEFAULT_GROUP } from '@/features/DataHub/constants/const';
import {
  buildExportBody,
  buildExportEndpoint
} from '@/features/DataHub/modules/Export/services/ExportRequestService';
import type {
  ExportRequestOverrides,
  ExportSettingsType
} from '@/features/DataHub/modules/Export/types';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

const snackbarKey = 'deleteExport';

export const useCreateExport = ({
  exportSettings,
  fromValue,
  toValue,
  symbols,
  userTimezone,
  userDateFormat,
  onMutate,
  onSuccess
}: {
  exportSettings: ExportSettingsType;
  fromValue: string;
  toValue: string | null;
  symbols: SymbolKey[];
  userTimezone: string;
  userDateFormat: string;
  onMutate?: () => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ExportRequestOverrides) => {
      const { type } = params;

      const effectiveFrom = params.from ?? fromValue;
      const effectiveTo = params.to ?? (toValue || null);
      const effectiveIncludeMetadata = params.includeMetadata ?? exportSettings.includeMetadata;
      const effectiveTimezone = params.timezone ?? userTimezone;
      const effectiveDateFormat = params.dateFormat ?? userDateFormat;

      const endpoint = buildExportEndpoint({
        type,
        fromValue: effectiveFrom,
        toValue: effectiveTo,
        includeMetadata: effectiveIncludeMetadata,
        timezone: effectiveTimezone,
        dateFormat: effectiveDateFormat
      });

      const body = buildExportBody(
        params.symbols || symbols,
        params.group || DATAHUB_DEFAULT_GROUP
      );

      return exportApi.postExport(endpoint, body);
    },
    mutationKey: ['dataHub', 'export', 'create'] as const,
    onMutate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pastExportsOptions.lists() });
      onSuccess?.();
    }
  });
};

export const useDeletePastExport = () => {
  const queryClient = useQueryClient();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await pastExportsApi.deletePastExport(id);
      return response;
    },
    mutationKey: [...pastExportsOptions.all(), 'delete'] as const,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: pastExportsOptions.lists() });

      const previousExports = queryClient.getQueryData<ExportItemType[]>(
        pastExportsOptions.lists()
      );

      queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
        old?.filter(item => item.id !== id)
      );

      return { previousExports };
    },
    onSuccess: (_response, id) => {
      showSuccessSnackbar('Past export has been deleted', snackbarKey);
      queryClient.removeQueries({ queryKey: pastExportsOptions.detail(id) });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: pastExportsOptions.lists() });
      }, 5000);
    },
    onError: (_error, _id, context) => {
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsOptions.lists(), context.previousExports);
      }
      showErrorSnackbar('Error deleting past export', 2000, snackbarKey);
    }
  });
};
